// ============================================================================
// RÉGLAGES → ACCÈS BACK-OFFICE — qui entre dans votre back-office, et ce qu'il y fait.
//
// UTILITÉ : cet écran ne montrait que le JOURNAL des connexions. On voyait donc qui était entré, sans
// pouvoir dire qui a le droit d'entrer, ni le changer — ajouter un collaborateur demandait une
// insertion SQL.
//
// ⚠️ CE N'EST PAS « OPÉRATEURS & DROITS », et les confondre serait la faute. Là-bas, les comptes de
// CAISSE : un code à quatre chiffres, un magasin, des droits de comptoir. Ici, les comptes WEB :
// une adresse e-mail, une double authentification, et une portée qui peut couvrir plusieurs magasins.
//
// ⚠️ LES RÔLES SONT LES TROIS QUE LA BASE PORTE, pas les quatre du dessin. Le modèle métier
// (gérant, responsable, comptable… avec leur carte de droits par module) vit dans la CONSOLE, qui le
// publie aux clients ; le redéclarer ici produirait deux résolutions de droits divergentes — « la
// Console montre un droit que l'application refuse ». L'écran DIT où ce modèle vit plutôt que d'en
// afficher une copie qu'il ne saurait pas appliquer (§23.0).
//
// 🔴 L'ÉCRAN NE MONTRE QUE VOTRE PÉRIMÈTRE, parce que le SERVEUR ne rend que lui : la table des comptes est commune à
//    tous les clients du back-office. Un compte qui ouvre aussi des magasins d'un autre client (un comptable) se voit,
//    avec le NOMBRE de ces magasins, jamais leur nom — et ne se modifie pas d'ici : lui retirer « ses » magasins à
//    l'aveugle lui couperait l'accès chez l'autre client.
//
// ⚠️ AUCUN JETON, AUCUN MOT DE PASSE ne transite par cet écran : inviter envoie un lien, réarmer la
// 2FA efface un secret. Rien de ce qui ouvre un compte n'est affiché ni transmis d'ici.
// ============================================================================
import { useCallback, useEffect, useId, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { alert, confirm, EmptyState, Icon, showToast, Switch, Trans, useOverlayPanel, type IconName } from '@orgazone/ui-web';
import { api, type AccessAccount, type AccessEvent, type AccessView, type AuthorizedStore } from '../api';
import { key, t, type Translatable } from '../i18n';
import { useStoreScope } from '../store-context';

const CARD: CSSProperties = {
  border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden',
};
const LABEL: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.09em',
  textTransform: 'uppercase', color: 'var(--ink-500)', fontWeight: 600, marginBottom: 7,
};
const GRID = 'minmax(210px,1fr) 140px 92px 130px 108px 96px';

function Pill({ bg, fg, children, height = 22 }: { bg: string; fg: string; children: ReactNode; height?: number }) {
  return (
    <span style={{
      height, padding: '0 10px', borderRadius: 'var(--r-pill)', background: bg, color: fg,
      fontSize: 'var(--t-meta)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5,
    }}>{children}</span>
  );
}

/** Teinte d'un rôle — les TROIS réels, jamais les quatre du dessin. */
const ROLE_TINT: Record<string, { bg: string; fg: string }> = {
  admin: { bg: 'var(--teal-100)', fg: 'var(--teal-800)' },
  operator: { bg: 'var(--ink-100)', fg: 'var(--ink-600)' },
  orgazone_staff: { bg: 'var(--ink-900)', fg: 'var(--paper)' },
};

const STATUS_TINT: Record<string, { bg: string; fg: string; label: Translatable }> = {
  active: { bg: 'var(--teal-100)', fg: 'var(--teal-800)', label: key('Actif') },
  invited: { bg: 'var(--blue-bg)', fg: 'var(--blue-fg)', label: key('Invité') },
  disabled: { bg: 'var(--ink-100)', fg: 'var(--ink-500)', label: key('Révoqué') },
};

/** Les initiales d'un nom — dérivées, jamais stockées. */
function initials(name: string): string {
  const m = name.trim().split(/\s+/).filter(Boolean);
  if (!m.length) return '?';
  return ((m[0]![0] ?? '') + (m.length > 1 ? m[m.length - 1]![0] ?? '' : '')).toUpperCase();
}

/** Une date lisible, ou l'absence DITE — « jamais » n'est pas une date vide. */
function quand(ms: number | null): string {
  if (ms == null) return t('jamais connecté');
  return new Date(ms).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * Horodatage du journal — jamais une chaîne ISO brute, qu'on relit mal quand on cherche « quand ».
 * ⚠️ ILLISIBLE → RENDUE TELLE QUELLE : un journal d'accès doit montrer ce qu'il PORTE, même mal formé.
 */
function instant(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

export function Acces() {
  const [view, setView] = useState<AccessView | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stores, setStores] = useState<AuthorizedStore[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  // ⚠️ LA LISTE EST CELLE DU MAGASIN ACTIF (le serveur la borne ainsi) : changer de magasin dans le bandeau la RELIT,
  //    sinon l'écran montrerait les comptes du magasin quitté sous le nom du nouveau.
  const scope = useStoreScope();
  const magasin = scope.view?.active ?? null;

  // ⚠️ GARDE DE FRAÎCHEUR : deux changements de magasin rapprochés lancent deux lectures — la réponse du magasin quitté,
  //    arrivée en dernier, réécrirait sinon la liste sous le nom du nouveau.
  useEffect(() => {
    let vivant = true;
    setFailure(null);
    api.accessAccounts()
      .then((v) => { if (vivant) setView(v); })
      .catch((e: unknown) => { if (vivant) setFailure(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [magasin?.gln]);
  useEffect(() => {
    // ⚠️ Best-effort ASSUMÉ : sans la liste, on n'offre pas de saisir un identifiant de magasin à la
    //    main — ce serait précisément la prise que le serveur refuse (« jamais plus que ce qu'on a »).
    // ⚠️ LA FORME EST VÉRIFIÉE, PAS SUPPOSÉE : la promesse ci-dessus n'était pas tenue — une réponse
    //    qui n'est pas un tableau (route absente, enveloppe inattendue) atterrissait dans l'état et
    //    faisait planter le rendu au premier `.map`. Une lecture ratée doit DÉGRADER, jamais casser.
    void api.stores()
      .then((r) => setStores(Array.isArray(r) ? r : []))
      .catch(() => setStores([]));
  }, []);

  const accounts = useMemo(() => view?.accounts ?? [], [view]);
  const opened = openId ? accounts.find((a) => a.id === openId) ?? null : null;
  // ⚠️ LE SUPPORT ORGAZONE ADMINISTRE AUSSI LES ACCÈS (« tous les droits sans exception ») — même règle que le serveur.
  const admin = view?.me.role === 'admin' || view?.me.role === 'orgazone_staff';

  const apres = useCallback((v: { accounts: AccessAccount[] }, message: string) => {
    setView((prev) => (prev ? { ...prev, accounts: v.accounts } : prev));
    showToast(message);
  }, []);

  const geste = useCallback(async (fn: () => Promise<{ accounts: AccessAccount[] }>, message: string, titre: string) => {
    setBusy(true);
    try { apres(await fn(), message); }
    catch (e) { await alert({ title: titre, message: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  }, [apres]);

  const kpis = useMemo(() => {
    const clients = accounts.filter((a) => !a.editorStaff);
    return [
      {
        label: t('Accès actifs'), value: String(clients.filter((a) => a.status === 'active').length),
        note: t('comptes de votre enseigne'),
        icon: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0', bg: 'var(--teal-100)', fg: 'var(--teal-800)',
      },
      {
        label: t('Invitations en attente'), value: String(clients.filter((a) => a.status === 'invited').length),
        note: t('lien envoyé, jamais utilisé'),
        icon: 'M4.5 5h15a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM3 7l9 7 9-7',
        bg: 'var(--blue-bg)', fg: 'var(--blue-fg)',
      },
      {
        label: t('Sans double authentification'),
        value: String(clients.filter((a) => a.status === 'active' && !a.twoFactor).length),
        note: t('à relancer — la 2FA protège l’accès à vos données'),
        icon: 'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01',
        bg: 'var(--amber-bg)', fg: 'var(--amber-fg)',
      },
      {
        label: t('Accès Orgazone'), value: String(accounts.filter((a) => a.editorStaff).length),
        note: t('assistance de l’éditeur, actions journalisées'),
        icon: 'M7 11h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2zM8 11V7a4 4 0 0 1 8 0v4',
        bg: 'var(--ink-100)', fg: 'var(--ink-500)',
      },
    ];
  }, [accounts]);

  if (failure !== null) {
    return (
      <div className="dcard" style={{ padding: 18 }}>
        <p role="alert" style={{ margin: 0, fontSize: 'var(--t-lead)', color: 'var(--danger-ink)' }}>
          {t('Les accès au back-office n’ont pas pu être lus : {failure}', { failure })}
        </p>
      </div>
    );
  }
  if (!view) return <div className="dcard" style={{ padding: 18, fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>{t('Lecture…')}</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>
            
            {t('Accès back-office')}
          </h1>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
            <Trans k='Qui entre dans votre back-office, et ce qu’il y fait. Les comptes de caisse se gèrent dans <1>Opérateurs & droits</1>.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></p>
        </div>
        {/* ⚠️ OFFERT SEULEMENT SI UN LIEN PEUT PARTIR : sans messagerie réglée, une invitation créerait
            un compte que personne ne pourrait activer. Le motif est dit à la place du bouton. */}
        {view.canInvite ? (
          <button type="button" className="btn-cta" style={{ height: 40 }} onClick={() => setInviting(true)}>
            
            {t('Inviter un collaborateur')}
          </button>
        ) : admin && view.inviteBlockedReason ? (
          <span style={{
            maxWidth: 320, fontSize: 'var(--t-meta)', color: 'var(--amber-fg)', lineHeight: 1.5,
            padding: '8px 11px', background: 'var(--amber-bg)', borderRadius: 'var(--r-sm)',
          }}>{t('Invitation impossible — {inviteBlockedReason}', { inviteBlockedReason: view.inviteBlockedReason })}</span>
        ) : null}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(185px,1fr))', gap: 8, marginBottom: 10 }}>
        {kpis.map((k) => (
          <div key={k.label} title={k.note} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
            border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)', minWidth: 0,
          }}>
            <span style={{
              width: 30, height: 30, borderRadius: 'var(--r-ctrl)', background: k.bg, color: k.fg,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={k.icon} /></svg>
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={{
                display: 'block', fontSize: 'var(--t-micro)', fontWeight: 700, letterSpacing: '.05em',
                textTransform: 'uppercase', color: 'var(--ink-500)', whiteSpace: 'nowrap',
                overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{k.label}</span>
              <span style={{
                display: 'block', fontSize: 'var(--t-strong)', fontWeight: 800, color: 'var(--ink-900)',
                fontVariantNumeric: 'tabular-nums', marginTop: 1,
              }}>{k.value}</span>
            </span>
          </div>
        ))}
      </div>

      <div style={{ ...CARD, marginBottom: 12 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 9, padding: '11px 14px',
          borderBottom: '1px solid var(--ink-100)',
        }}>
          <span style={{ flex: 1, fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>
            
            {t('Comptes ayant accès')}
            {/* ⚠️ LA RÈGLE SE DIT : sans cette ligne, un compte qui ouvre plusieurs magasins ne sait pas pourquoi un
                collègue d'un autre magasin n'apparaît pas — ni, avant, pourquoi tous apparaissaient. */}
            {magasin && (
              <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 400, color: 'var(--ink-500)' }}>
                {t('Les comptes qui ouvrent le magasin « {magasin} », et l’assistance Orgazone.', { magasin: magasin.name })}
              </span>
            )}
          </span>
          <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>
            {t('{n} compte', { n: accounts.length })}
          </span>
        </div>
        <AccountsTable accounts={accounts} onOpen={setOpenId} />
      </div>

      <AccessLog />

      {opened && (
        <AccountSheet
          account={opened} me={view.me} stores={stores} busy={busy}
          onClose={() => setOpenId(null)}
          onRole={(id, role) => geste(() => api.setAccessRole(id, role), t('Rôle enregistré.'), t('Rôle refusé'))}
          onGrant={(id, grant, on) => geste(
            () => api.setAccessGrant(id, grant, on),
            on ? t('Droit accordé.') : t('Droit retiré.'),
            t('Refusé'),
          )}
          onStatus={(id, active) => geste(
            () => api.setAccessStatus(id, active),
            active ? t('Accès réactivé.') : t('Accès révoqué — les sessions ouvertes sont fermées.'),
            t('Refusé'),
          )}
          onStores={(id, glns) => geste(() => api.setAccessStores(id, glns), t('Portée enregistrée.'), t('Portée refusée'))}
          onResetTwoFactor={(id) => geste(
            () => api.resetAccessTwoFactor(id),
            t('Double authentification réarmée — le compte devra en poser une nouvelle.'),
            t('Refusé'),
          )}
          onResend={async (email) => {
            setBusy(true);
            try { await api.resendAccessInvite(email); showToast(t('Lien renvoyé.')); }
            catch (e) { await alert({ title: t('Envoi refusé'), message: e instanceof Error ? e.message : String(e) }); }
            finally { setBusy(false); }
          }}
        />
      )}
      {inviting && (
        <InviteDialog
          stores={stores}
          onClose={() => setInviting(false)}
          onDone={(v) => { setInviting(false); apres(v, t('Invitation envoyée — le lien part par e-mail.')); }}
        />
      )}
    </div>
  );
}

// ── LA LISTE ────────────────────────────────────────────────────────────────────────────────────

function AccountsTable({ accounts, onOpen }: { accounts: AccessAccount[]; onOpen: (id: string) => void }) {
  const head: CSSProperties = {
    display: 'grid', gridTemplateColumns: GRID, gap: 8, alignItems: 'center', padding: '9px 14px',
    background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', minWidth: 820,
    fontSize: 'var(--t-sub)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase',
    color: 'var(--ink-500)',
  };
  if (!accounts.length) {
    return (
      <EmptyState
        icon="users"
        title={t('Aucun compte n’ouvre ce back-office')}
        note={t('Invitez un collaborateur : il recevra un lien pour poser son mot de passe et armer sa double authentification.')}
      />
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={head}>
        {[t('Compte'), t('Rôle'), t('Sécurité'), t('Magasins'), t('Dernier accès'), t('État')].map((h) => <span key={h}>{h}</span>)}
      </div>
      <div style={{ minWidth: 820 }}>
        {accounts.map((a) => {
          const tint = ROLE_TINT[a.role] ?? ROLE_TINT.operator!;
          const st = STATUS_TINT[a.status] ?? STATUS_TINT.disabled!;
          return (
            <div key={a.id} role="button" tabIndex={0} onClick={() => onOpen(a.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(a.id); } }}
              style={{
                display: 'grid', gridTemplateColumns: GRID, gap: 8, alignItems: 'center', padding: '8px 14px',
                borderBottom: '1px solid var(--ink-100)', cursor: 'pointer',
                opacity: a.status === 'disabled' ? 0.6 : 1,
              }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <span style={{
                  width: 32, height: 32, borderRadius: 'var(--r-pill)', flexShrink: 0,
                  background: a.editorStaff ? 'var(--ink-900)' : a.status === 'active' ? 'var(--teal-700)' : 'var(--ink-200)',
                  color: a.editorStaff || a.status === 'active' ? 'var(--paper)' : 'var(--ink-500)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 'var(--t-meta)', fontWeight: 700,
                }}>{initials(a.name)}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{
                    display: 'block', fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{a.name}</span>
                  <span style={{
                    display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{a.email}</span>
                </span>
              </span>
              <span><Pill bg={tint.bg} fg={tint.fg}>{t(a.roleLabel)}</Pill></span>
              <span>
                {a.twoFactor
                  ? <Pill bg="var(--teal-100)" fg="var(--teal-800)" height={20}>2FA</Pill>
                  : <Pill bg="var(--amber-bg)" fg="var(--amber-fg)" height={20}>{t('Sans 2FA')}</Pill>}
              </span>
              <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-600)' }}>
                {a.editorStaff ? t('tous (éditeur)')
                  : a.stores.length === 0 ? <span style={{ color: 'var(--amber-fg)' }}>{t('aucun')}</span>
                    : a.stores.length === 1 ? (a.stores[0]!.name ?? a.stores[0]!.gln)
                      : t('{n} magasin', { n: a.stores.length })}
                {a.outsideStores > 0 && (
                  <span style={{ display: 'block', color: 'var(--ink-400)' }}>{t('+ {n} magasin hors de votre périmètre', { n: a.outsideStores })}</span>
                )}
              </span>
              <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{quand(a.lastLoginAt)}</span>
              <span><Pill bg={st.bg} fg={st.fg}>{t(st.label)}</Pill></span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── LE JOURNAL, conservé de la version précédente ───────────────────────────────────────────────

/**
 * LE JOURNAL DES ACCÈS — la moitié qui existait déjà, et qui reste le cœur de la réciprocité.
 * ⚠️ « Un accès d'assistance invisible est un accès suspect ; visible et journalisé, c'est un gage de
 * confiance » (doctrine d'identité §4ter). Les tentatives REFUSÉES y figurent aussi : un ticket
 * rejoué est précisément ce qu'il faut pouvoir constater.
 */
function AccessLog() {
  const [rows, setRows] = useState<AccessEvent[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    api.accessJournal()
      .then((r) => { if (vivant) { setRows(r); setFailure(null); } })
      .catch((e: unknown) => { if (vivant) setFailure(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, []);

  return (
    <div style={CARD}>
      <div style={{ padding: '11px 14px', borderBottom: '1px solid var(--ink-100)' }}>
        <span style={{ fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Journal des accès')}</span>
        <p style={{ margin: '3px 0 0', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
          <Trans k='Chaque entrée de l’assistance Orgazone dans ce back-office apparaît ici, avec qui est entré et quand. Les tentatives <1>refusées</1> y figurent aussi.' parts={[(x) => <strong>{x}</strong>]} /></p>
      </div>
      {failure !== null ? (
        <p role="alert" style={{ margin: 0, padding: 16, fontSize: 'var(--t-lead)', color: 'var(--danger-ink)' }}>
          {t('Le journal n’a pas pu être lu : {failure}', { failure })}
        </p>
      ) : !rows ? (
        <div style={{ padding: 16, fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>{t('Lecture…')}</div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon="lock" compact
          title={t('Aucun accès d’assistance à ce jour')}
          note={t('Personne d’Orgazone n’est entré dans ce back-office. Toute entrée future s’inscrira ici.')}
        />
      ) : (
        <div>
          {rows.map((e, i) => (
            <div key={e.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px',
              borderBottom: i === rows.length - 1 ? 'none' : '1px solid var(--ink-100)',
            }}>
              {/* ⚠️ L'HEURE EN TÊTE, en chasse fixe : c'est par elle qu'on relit un journal, et des
                  horodatages qui ne s'alignent pas se comparent mal. */}
              <span className="mono" style={{
                fontSize: 'var(--t-meta)', color: 'var(--ink-500)', whiteSpace: 'nowrap', paddingTop: 2,
              }}>{instant(e.at)}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 'var(--t-body)', color: 'var(--ink-900)', lineHeight: 1.5 }}>
                  {e.text}
                </span>
                <span className="mono" style={{
                  display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 2,
                }}>
                  {e.storeName} · {e.gln}{e.actor ? ` · ${e.actor}` : ''}{e.ip ? ` · ${e.ip}` : ''}
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── LA FICHE D'UN COMPTE ────────────────────────────────────────────────────────────────────────

function Modal({ width, label, children, onClose }: {
  width: number;
  /** Le nom de la fenêtre pour une aide technique — son titre visible. */
  label: string;
  children: ReactNode;
  onClose: () => void;
}) {
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  const { panelRef } = useOverlayPanel({ open: true, onClose });
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 900, display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: 20,
    }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(12,14,16,.42)' }} />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label={label} style={{
        position: 'relative', width: `min(96vw,${width}px)`, maxHeight: '90vh', background: 'var(--paper)',
        borderRadius: 'var(--r-lg)', boxShadow: '0 28px 70px -10px rgba(0,0,0,.45)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>{children}</div>
    </div>
  );
}

function Bloc({ icon, title, children }: { icon: IconName; title: string; children: ReactNode }) {
  return (
    <div style={{ padding: '12px 13px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
        <span style={{ color: 'var(--ink-500)', display: 'flex' }}><Icon name={icon} size={14} stroke={1.9} /></span>
        <span style={{ ...LABEL, marginBottom: 0 }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function AccountSheet({ account, me, stores, busy, onClose, onRole, onGrant, onStatus, onStores, onResetTwoFactor, onResend }: {
  account: AccessAccount;
  me: { id: string; role: string };
  stores: AuthorizedStore[];
  busy: boolean;
  onClose: () => void;
  onRole: (id: string, role: string) => Promise<void>;
  onGrant: (id: string, grant: string, on: boolean) => Promise<void>;
  onStatus: (id: string, active: boolean) => Promise<void>;
  onStores: (id: string, glns: string[]) => Promise<void>;
  onResetTwoFactor: (id: string) => Promise<void>;
  onResend: (email: string) => Promise<void>;
}) {
  const [glns, setGlns] = useState<string[]>(account.stores.map((s) => s.gln));
  const admin = me.role === 'admin' || me.role === 'orgazone_staff';
  const soiMeme = me.id === account.id;
  // ⚠️ UNE SEULE RAISON PAR REFUS, et elle s'affiche : un bouton grisé sans motif fait chercher.
  const verrou = account.editorStaff
    ? t('Ce compte appartient à l’assistance Orgazone : il se gère depuis la Console de l’éditeur.')
    : account.outsideStores > 0 ? t('Ce compte ouvre aussi des magasins hors de votre périmètre : il se modifie depuis la Console, ou par un administrateur qui les ouvre tous.')
    : !admin ? t('Seul un administrateur peut modifier un accès.')
      : soiMeme ? t('Vous ne pouvez pas modifier votre propre accès : personne ne pourrait vous rouvrir la porte.')
        : null;
  const modifiable = verrou === null;
  const portéeChangée = JSON.stringify([...glns].sort()) !== JSON.stringify(account.stores.map((s) => s.gln).sort());

  return (
    <Modal width={720} label={account.name} onClose={onClose}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px',
        borderBottom: '1px solid var(--ink-200)', flexShrink: 0,
      }}>
        <span style={{
          width: 42, height: 42, borderRadius: 'var(--r-pill)', flexShrink: 0,
          background: account.editorStaff ? 'var(--ink-900)' : 'var(--teal-700)', color: 'var(--paper)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 'var(--t-strong)', fontWeight: 700,
        }}>{initials(account.name)}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--t-h3)', fontWeight: 700, color: 'var(--ink-900)' }}>{account.name}</span>
            <Pill bg={(STATUS_TINT[account.status] ?? STATUS_TINT.disabled!).bg}
              fg={(STATUS_TINT[account.status] ?? STATUS_TINT.disabled!).fg} height={20}>
              {t((STATUS_TINT[account.status] ?? STATUS_TINT.disabled!).label)}
            </Pill>
          </span>
          <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 2 }}>
            {t('{email} · dernier accès {v}', { email: account.email, v: quand(account.lastLoginAt) })}
          </span>
        </span>
        <button type="button" onClick={onClose} aria-label={t('Fermer')} style={{
          width: 34, height: 34, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)',
          color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, cursor: 'pointer',
        }}><Icon name="x" size={15} stroke={2.2} /></button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 11 }}>
        {verrou && (
          <div style={{
            display: 'flex', gap: 9, padding: '10px 13px', borderRadius: 'var(--r-sm)',
            background: 'var(--ink-50)', border: '1px solid var(--ink-200)',
            fontSize: 'var(--t-meta)', color: 'var(--ink-600)', lineHeight: 1.55,
          }}>
            <Icon name="lock" size={14} stroke={1.9} />
            <span>{verrou}</span>
          </div>
        )}

        <Bloc icon="user" title={t('Son identité')}>
          <p style={{ margin: 0, fontSize: 'var(--t-meta)', color: 'var(--ink-600)', lineHeight: 1.6 }}>
            <Trans k='Le nom, le mot de passe et la double authentification appartiennent au compte lui-même : ils <1>ne se modifient pas ici</1>. Ce que vous réglez sur cette fiche, c’est ce que ce compte a le droit d’ouvrir.' parts={[(x) => <strong>{x}</strong>]} /></p>
        </Bloc>

        <Bloc icon="users" title={t('Rôle')}>
          <select aria-label={t('Rôle')} className="field-input" style={{ height: 36, width: '100%' }} value={account.role}
            disabled={!modifiable || busy}
            onChange={(e) => { void onRole(account.id, e.target.value); }}>
            {account.editorStaff && <option value="orgazone_staff">{t('Support Orgazone')}</option>}
            <option value="admin">{t('Administrateur')}</option>
            <option value="operator">{t('Opérateur')}</option>
          </select>
          <p style={{ margin: '6px 0 0', fontSize: 'var(--t-micro)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
            {/* ⚠️ ON DIT OÙ VIT LE MODÈLE MÉTIER plutôt que d'en afficher une copie que ce back-office
                ne saurait pas appliquer : deux résolutions de droits divergentes, c'est un utilisateur
                à qui l'une montre un droit que l'autre refuse. */}
            <Trans k='<1>Administrateur</1> règle les accès et les paramètres ; <2>Opérateur</2> exploite le back-office sans y toucher. La carte de droits détaillée, par module, est publiée par Orgazone et ne se règle pas depuis cet écran.' parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]} /></p>
        </Bloc>

        {/* ⚠️ UN DROIT QU'AUCUN RÔLE NE DONNE (décision utilisateur : « un droit spécifique uniquement pour ça ») — même un
            administrateur ne l'a que si un autre le lui accorde. Le support Orgazone le détient d'office. */}
        <Bloc icon="trash" title={t('Droits particuliers')}>
          <Switch
            layout="card"
            checked={account.editorStaff || account.grants.includes('articles.delete')}
            disabled={!modifiable || busy}
            onChange={(on) => { void onGrant(account.id, 'articles.delete', on); }}
            label={t('Supprimer des articles')}
            hint={account.editorStaff
              ? t('Le support Orgazone le détient d’office.')
              : t('Un geste rare : seulement un article jamais vendu ni stocké, absent des articles rapides, quand toutes les caisses sont en ligne. La suppression est définitive.')}
          />
        </Bloc>

        <Bloc icon="store" title={t('Magasins accessibles')}>
          {stores.length === 0 ? (
            <p style={{ margin: 0, fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
              
              {t('La liste de vos magasins n’a pas pu être lue — rouvrez cette fiche.')}
            </p>
          ) : (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {stores.map((s) => {
                  const on = glns.includes(s.gln);
                  return (
                    <button key={s.gln} type="button" disabled={!modifiable || busy}
                      onClick={() => setGlns((prev) => (on ? prev.filter((g) => g !== s.gln) : [...prev, s.gln]))}
                      style={{
                        height: 30, padding: '0 11px', borderRadius: 'var(--r-pill)',
                        cursor: modifiable ? 'pointer' : 'default',
                        border: `1px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`,
                        background: on ? 'var(--teal-100)' : 'var(--paper)',
                        color: on ? 'var(--teal-800)' : 'var(--ink-600)',
                        fontSize: 'var(--t-dense)', fontWeight: 600,
                      }}>{s.name}</button>
                  );
                })}
              </div>
              <p style={{ margin: '7px 0 0', fontSize: 'var(--t-micro)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
                
                {t('Vous ne pouvez donner accès qu’aux magasins que vous ouvrez vous-même.')}
                {account.outsideStores > 0 && (
                  <> {t('Il ouvre aussi {n} magasin hors de votre périmètre, qui n’est pas montré.', { n: account.outsideStores })}</>
                )}
                {account.stores.length === 0 && !account.editorStaff && (
                  <> <Trans k='⚠️ Ce compte n’a <1>aucun magasin</1> : il ne peut rien ouvrir.' parts={[(x) => <strong>{x}</strong>]} /></>
                )}
              </p>
              {modifiable && portéeChangée && (
                <button type="button" className="btn-cta" style={{ height: 34, marginTop: 8 }} disabled={busy}
                  onClick={() => { void onStores(account.id, glns); }}>
                  
                  {t('Enregistrer la portée')}
                </button>
              )}
            </>
          )}
        </Bloc>

        <Bloc icon="lock" title={t('Sécurité')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            {account.twoFactor
              ? <Pill bg="var(--teal-100)" fg="var(--teal-800)" height={20}>{t('Double authentification armée')}</Pill>
              : <Pill bg="var(--amber-bg)" fg="var(--amber-fg)" height={20}>{t('Sans double authentification')}</Pill>}
            {account.status === 'invited' && (
              <button type="button" className="btn-ghost" style={{ height: 30 }}
                // ⚠️ LA MÊME RÈGLE QUE LE SERVEUR : un compte de l'éditeur ou hors du périmètre ne reçoit pas de lien d'ici.
                disabled={busy || !admin || account.editorStaff || account.outsideStores > 0}
                onClick={() => { void onResend(account.email); }}>
                
                {t('Renvoyer le lien')}
              </button>
            )}
            {/* ⚠️ RÉARMER N'EST PAS DISPENSER : le secret est effacé, donc le compte devra en poser un
                nouveau. Les sessions ouvertes sont fermées dans le même geste — sans quoi un appareil
                volé continuerait de travailler avec l'ancienne authentification. */}
            {modifiable && account.twoFactor && (
              <button type="button" className="btn-ghost" style={{ height: 30 }} disabled={busy}
                onClick={async () => {
                  const ok = await confirm({
                    title: t('Réarmer la double authentification de « {name} » ?', { name: account.name }),
                    message: t('Son secret actuel est effacé et ses sessions ouvertes sont fermées. Il devra poser une nouvelle double authentification à sa prochaine connexion.'),
                    confirmLabel: t('Réarmer'),
                    tone: 'danger',
                  });
                  if (ok) void onResetTwoFactor(account.id);
                }}>
                
                {t('Réarmer la 2FA')}
              </button>
            )}
          </div>
        </Bloc>
      </div>

      <div style={{
        display: 'flex', gap: 10, alignItems: 'center', padding: '12px 20px',
        borderTop: '1px solid var(--ink-200)', flexShrink: 0,
      }}>
        {modifiable && account.status !== 'invited' && (
          <button type="button" style={{
            height: 34, padding: '0 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
            background: 'var(--paper)', color: account.status === 'active' ? 'var(--danger-ink)' : 'var(--ink-700)',
            fontSize: 'var(--t-body)', fontWeight: 600, cursor: busy ? 'default' : 'pointer',
          }} disabled={busy}
            onClick={async () => {
              if (account.status !== 'active') { void onStatus(account.id, true); return; }
              const ok = await confirm({
                title: t('Révoquer l’accès de « {name} » ?', { name: account.name }),
                message: t('Ses sessions ouvertes sont fermées immédiatement. Le compte est conservé — vous pourrez le réactiver.'),
                confirmLabel: t('Révoquer'),
                tone: 'danger',
              });
              if (ok) void onStatus(account.id, false);
            }}>
            {account.status === 'active' ? t('Révoquer l’accès') : t('Réactiver l’accès')}
          </button>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn-cta" onClick={onClose}>{t('Terminé')}</button>
      </div>
    </Modal>
  );
}

// ── L'INVITATION ────────────────────────────────────────────────────────────────────────────────

function InviteDialog({ stores, onClose, onDone }: {
  stores: AuthorizedStore[];
  onClose: () => void;
  onDone: (v: { accounts: AccessAccount[] }) => void;
}) {
  /** Le préfixe des identifiants qui relient chaque champ au texte qui le présente. */
  const fieldId = useId();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('operator');
  const [glns, setGlns] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const pret = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim()) && name.trim().length > 0 && glns.length > 0;

  return (
    <Modal width={520} label={t('Inviter un collaborateur')} onClose={onClose}>
      <div style={{ padding: '22px 22px 18px', overflowY: 'auto' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--t-h3)', fontWeight: 700, color: 'var(--ink-900)' }}>
          
          {t('Inviter un collaborateur')}
        </h3>
        <p style={{ margin: '4px 0 14px', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
          <Trans k='Il recevra un lien pour poser son mot de passe et armer sa double authentification. <1>Vous n’avez aucun mot de passe à créer ni à transmettre.</1>' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div id={`${fieldId}-1`} style={LABEL}>{t('Adresse e-mail professionnelle')}</div>
            <input aria-labelledby={`${fieldId}-1`} className="field-input" style={{ height: 38, width: '100%' }} value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder={t('prenom.nom@enseigne.fr')} autoComplete="off" />
          </div>
          <div>
            <div id={`${fieldId}-2`} style={LABEL}>{t('Nom affiché')}</div>
            <input aria-labelledby={`${fieldId}-2`} className="field-input" style={{ height: 38, width: '100%' }} value={name}
              onChange={(e) => setName(e.target.value)} placeholder={t('Prénom Nom')} />
          </div>
          <div>
            <div id={`${fieldId}-0`} style={LABEL}>{t('Rôle')}</div>
            <select aria-labelledby={`${fieldId}-0`} className="field-input" style={{ height: 38, width: '100%' }} value={role}
              onChange={(e) => setRole(e.target.value)}>
              <option value="operator">{t('Opérateur — exploite le back-office')}</option>
              <option value="admin">{t('Administrateur — règle aussi les accès')}</option>
            </select>
          </div>
          <div>
            <div style={LABEL}>{t('Magasins accessibles')}</div>
            {stores.length === 0 ? (
              <p style={{ margin: 0, fontSize: 'var(--t-meta)', color: 'var(--amber-fg)' }}>
                
                {t('Vos magasins n’ont pas pu être lus : sans eux, l’invitation ne peut pas poser de portée.')}
              </p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {stores.map((s) => {
                  const on = glns.includes(s.gln);
                  return (
                    <button key={s.gln} type="button"
                      onClick={() => setGlns((p) => (on ? p.filter((g) => g !== s.gln) : [...p, s.gln]))}
                      style={{
                        height: 30, padding: '0 11px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
                        border: `1px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`,
                        background: on ? 'var(--teal-100)' : 'var(--paper)',
                        color: on ? 'var(--teal-800)' : 'var(--ink-600)',
                        fontSize: 'var(--t-dense)', fontWeight: 600,
                      }}>{s.name}</button>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{
            display: 'flex', gap: 8, padding: '10px 12px', border: '1px solid var(--ink-200)',
            borderRadius: 'var(--r-sm)', background: 'var(--ink-50)',
            fontSize: 'var(--t-meta)', color: 'var(--ink-600)', lineHeight: 1.55,
          }}>
            <Icon name="lock" size={13} stroke={2} />
            <span>
              <Trans k='Lien à <1>usage unique</1>. Cette invitation apparaît au journal, chez vous comme chez Orgazone. Sans magasin, le compte ne pourrait rien ouvrir : au moins un est requis.' parts={[(x) => <strong>{x}</strong>]} /></span>
          </div>
          {failure && (
            <div role="alert" style={{ fontSize: 'var(--t-dense)', color: 'var(--danger-ink)', lineHeight: 1.5 }}>{failure}</div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
          <button type="button" className="btn-ghost" onClick={onClose}>{t('Annuler')}</button>
          <button type="button" className="btn-cta" disabled={busy || !pret}
            onClick={async () => {
              setBusy(true); setFailure(null);
              try { onDone(await api.inviteAccess(email.trim(), name.trim(), role, glns)); }
              catch (e) { setFailure(e instanceof Error ? e.message : String(e)); }
              finally { setBusy(false); }
            }}>
            {busy ? t('Envoi…') : t('Envoyer l’invitation')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
